<template>
  <div class="history-panel">
    <div class="history-header">
      <h3>📚 处理历史</h3>
      <div class="history-stats">
        <span class="stat">总计 {{ stats.total }}</span>
        <span class="stat success">成功 {{ stats.successful }}</span>
        <span class="stat failed" v-if="stats.failed > 0">失败 {{ stats.failed }}</span>
      </div>
      <button v-if="history.length > 0" class="btn-clear" @click="confirmClearHistory">
        清空历史
      </button>
    </div>

    <div v-if="history.length === 0" class="empty-history">
      <div class="empty-icon">📝</div>
      <p>还没有处理记录</p>
      <p class="empty-tip">开始上传图片并处理后，历史记录将显示在这里</p>
    </div>

    <div v-else class="history-list">
      <div
        v-for="record in history"
        :key="record.id"
        :class="['history-item', { failed: !record.success }]"
      >
        <div class="history-content">
          <div class="history-info">
            <div class="operation-text">{{ truncateText(record.operation, 60) }}</div>
            <div class="history-meta">
              <span class="time">{{ formatTime(record.timestamp) }}</span>
              <span class="images-count">
                {{ record.originalImages.length }}张图片
              </span>
              <span v-if="record.success" class="status success">✅ 成功</span>
              <span v-else class="status failed">❌ 失败</span>
            </div>
          </div>

          <div v-if="record.success && record.resultImages.length > 0" class="result-preview">
            <img
              :src="record.resultImages[0]"
              :alt="'处理结果预览'"
              @click="viewResult(record)"
              class="preview-image"
            />
            <span v-if="record.resultImages.length > 1" class="image-count">
              +{{ record.resultImages.length - 1 }}
            </span>
          </div>
        </div>

        <div class="history-actions">
          <button class="action-btn" @click="reprocessWithSameOperation(record)" title="使用相同操作">
            🔄
          </button>
          <button class="action-btn" @click="viewDetails(record)" title="查看详情">
            👁️
          </button>
          <button class="action-btn delete" @click="deleteRecord(record.id)" title="删除记录">
            🗑️
          </button>
        </div>
      </div>
    </div>

    <!-- 详情模态框 -->
    <div v-if="selectedRecord" class="modal-overlay" @click="closeDetails">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h4>处理详情</h4>
          <button class="close-btn" @click="closeDetails">×</button>
        </div>
        <div class="modal-body">
          <div class="detail-section">
            <h5>操作描述</h5>
            <p>{{ selectedRecord.operation }}</p>
          </div>
          <div class="detail-section">
            <h5>原始图片 ({{ selectedRecord.originalImages.length }}张)</h5>
            <div class="original-images">
              <div v-for="(img, index) in selectedRecord.originalImages" :key="index" class="img-info">
                <span>{{ img.name }}</span>
                <span class="size">{{ formatFileSize(img.size) }}</span>
              </div>
            </div>
          </div>
          <div v-if="selectedRecord.success" class="detail-section">
            <h5>处理结果 ({{ selectedRecord.resultImages.length }}张)</h5>
            <div class="result-images">
              <img
                v-for="(img, index) in selectedRecord.resultImages"
                :key="index"
                :src="img"
                :alt="`结果 ${index + 1}`"
                @click="viewFullscreen(img)"
                class="result-image"
              />
            </div>
          </div>
          <div class="detail-section">
            <h5>处理时间</h5>
            <p>{{ new Date(selectedRecord.timestamp).toLocaleString('zh-CN') }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getHistory, deleteHistoryRecord, clearHistory, formatTime, getHistoryStats } from '../utils/history.js'

export default {
  name: 'HistoryPanel',
  emits: ['reprocess', 'view-result'],
  data () {
    return {
      history: [],
      stats: { total: 0, successful: 0, failed: 0 },
      selectedRecord: null
    }
  },
  mounted () {
    this.loadHistory()
  },
  methods: {
    loadHistory () {
      this.history = getHistory()
      this.stats = getHistoryStats()
    },
    formatTime,
    formatFileSize (bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
    },
    truncateText (text, length) {
      return text.length > length ? text.substring(0, length) + '...' : text
    },
    reprocessWithSameOperation (record) {
      this.$emit('reprocess', record.operation)
    },
    viewResult (record) {
      this.$emit('view-result', record.resultImages)
    },
    viewDetails (record) {
      this.selectedRecord = record
    },
    closeDetails () {
      this.selectedRecord = null
    },
    viewFullscreen (imageUrl) {
      // 触发父组件的全屏查看
      this.$emit('view-result', [imageUrl])
    },
    deleteRecord (id) {
      if (confirm('确定要删除这条历史记录吗？')) {
        deleteHistoryRecord(id)
        this.loadHistory()
      }
    },
    confirmClearHistory () {
      if (confirm('确定要清空所有历史记录吗？此操作不可恢复。')) {
        clearHistory()
        this.loadHistory()
      }
    }
  }
}
</script>

<style scoped>
.history-panel {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
}

.history-header h3 {
  margin: 0;
  color: #2c3e50;
}

.history-stats {
  display: flex;
  gap: 12px;
  align-items: center;
}

.stat {
  font-size: 14px;
  padding: 4px 8px;
  border-radius: 12px;
  background: #f8f9fa;
  color: #495057;
}

.stat.success {
  background: #d4edda;
  color: #155724;
}

.stat.failed {
  background: #f8d7da;
  color: #721c24;
}

.btn-clear {
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-clear:hover {
  background: #c82333;
}

.empty-history {
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.empty-tip {
  font-size: 14px;
  opacity: 0.8;
}

.history-list {
  max-height: 400px;
  overflow-y: auto;
}

.history-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  margin-bottom: 12px;
  transition: all 0.3s ease;
}

.history-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.history-item.failed {
  border-color: #f5c6cb;
  background: #f8f9fa;
}

.history-content {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.history-info {
  flex: 1;
}

.operation-text {
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 6px;
  line-height: 1.4;
}

.history-meta {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #6c757d;
  align-items: center;
  flex-wrap: wrap;
}

.status.success {
  color: #28a745;
}

.status.failed {
  color: #dc3545;
}

.result-preview {
  position: relative;
  flex-shrink: 0;
}

.preview-image {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 6px;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.preview-image:hover {
  transform: scale(1.1);
}

.image-count {
  position: absolute;
  bottom: -2px;
  right: -2px;
  background: #74b9ff;
  color: white;
  font-size: 10px;
  padding: 2px 4px;
  border-radius: 8px;
  font-weight: 600;
}

.history-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.action-btn {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 6px 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
}

.action-btn:hover {
  background: #e9ecef;
}

.action-btn.delete:hover {
  background: #dc3545;
  color: white;
  border-color: #dc3545;
}

/* 模态框样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px 0;
  border-bottom: 1px solid #dee2e6;
  margin-bottom: 20px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6c757d;
}

.modal-body {
  padding: 0 24px 24px;
}

.detail-section {
  margin-bottom: 20px;
}

.detail-section h5 {
  margin-bottom: 8px;
  color: #2c3e50;
  font-size: 16px;
}

.original-images {
  display: grid;
  gap: 8px;
}

.img-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  font-size: 14px;
}

.size {
  color: #6c757d;
  font-size: 12px;
}

.result-images {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
}

.result-image {
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 6px;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.result-image:hover {
  transform: scale(1.05);
}

@media (max-width: 768px) {
  .history-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .history-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .result-images {
    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  }

  .result-image {
    height: 80px;
  }
}
</style>
